import { useState, useEffect } from "react";
import { Send, X, RefreshCw, Check, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Resend Share Modal — re-deliver an existing active link to its coach.
 * Optionally edit the personal note before resending.
 */
export function ResendShareModal({ open, share, athleteId, suggestedNote, onClose, onResent }) {
  const [note, setNote] = useState(suggestedNote || share?.personal_note || "");
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiGenerated, setAiGenerated] = useState(false);

  // When opened from a nudge, auto-fetch the AI-personalized note
  useEffect(() => {
    setNote(suggestedNote || share?.personal_note || "");
    setAiGenerated(false);
    if (open && share && suggestedNote && athleteId) {
      // Replace generic suggestion with AI-personalized note
      generateAINote();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [share?.share_id, suggestedNote, open]);

  const generateAINote = async () => {
    if (!share || !athleteId) return;
    setAiLoading(true);
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${API}/api/players/${athleteId}/shares/${share.share_id}/ai-followup`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
      });
      const txt = await r.text();
      let data = {};
      try { data = JSON.parse(txt); } catch {}
      if (r.ok && data.note) {
        setNote(data.note);
        setAiGenerated(!!data.ai_generated);
        if (!data.ai_generated && data.fallback_reason) {
          toast.message("Using basic template (AI unavailable)", { description: data.fallback_reason });
        }
      }
    } catch (e) {
      // silently keep the generic suggestion
    } finally {
      setAiLoading(false);
    }
  };

  if (!open || !share) return null;

  const close = () => { setNote(share.personal_note || ""); setAiGenerated(false); onClose && onClose(); };
  const isNudge = !!suggestedNote;

  const handleResend = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const r = await fetch(`${API}/api/players/${athleteId}/shares/${share.share_id}/resend`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ note: note.trim() }),
      });
      const txt = await r.text();
      let data = {};
      try { data = JSON.parse(txt); } catch {}
      if (!r.ok) throw new Error(data.detail || "Resend failed");
      const emailOk = data.email && data.email.status === "success";
      toast.success(emailOk ? `Email re-sent to ${share.coach_email}` : `Link refreshed (email service unavailable on this environment)`);
      onResent && onResent();
      close();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="resend-share-modal">
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center">
              <RefreshCw size={16} className="text-white" />
            </div>
            <div>
              <h2 className="text-white font-bold text-base leading-tight">Resend Email to Coach</h2>
              <p className="text-blue-100 text-[11px]">{share.coach_name || share.coach_email}{share.coach_school ? ` · ${share.coach_school}` : ""}</p>
            </div>
          </div>
          <button onClick={close} className="text-white/70 hover:text-white" data-testid="resend-modal-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className={`rounded-lg p-3 text-xs leading-relaxed border flex gap-2 ${isNudge ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-blue-50 border-blue-200 text-blue-900"}`}>
            {isNudge && <Sparkles size={14} className="flex-shrink-0 mt-0.5 text-amber-600" />}
            <p>
              {isNudge ? (
                aiLoading ? (
                  <><strong>Drafting an AI-personalized follow-up…</strong> we'll reference {share.coach_school || "the program"} and your top verified metric.</>
                ) : aiGenerated ? (
                  <><strong>AI-personalized follow-up:</strong> drafted based on your verified metrics and {share.coach_school || "the program"}. Edit anything you'd like before sending.</>
                ) : (
                  <><strong>Smart follow-up:</strong> we've drafted a polite nudge below — edit it however you like, then send. The same active link will be re-delivered to <strong>{share.coach_email}</strong>.</>
                )
              ) : (
                <>We'll re-send the same active link to <strong>{share.coach_email}</strong>. The link doesn't change — it's the same one they already have.</>
              )}
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-xs font-bold text-gray-700 uppercase tracking-wider">
                Personal Message <span className="text-gray-400 normal-case font-normal">(edit before resending — optional)</span>
              </label>
              {isNudge && (
                <button
                  onClick={generateAINote}
                  disabled={aiLoading}
                  className="text-[11px] font-bold text-amber-700 hover:text-amber-900 flex items-center gap-1 disabled:opacity-50"
                  data-testid="ai-regenerate-btn"
                >
                  <Sparkles size={10} /> {aiLoading ? "Generating…" : aiGenerated ? "Regenerate" : "Draft with AI"}
                </button>
              )}
            </div>
            <div className="relative">
              <textarea
                rows={5}
                value={note}
                onChange={(e) => { setNote(e.target.value); setAiGenerated(false); }}
                placeholder="Hi Coach — just a quick follow-up..."
                className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none resize-none transition ${aiLoading ? "bg-amber-50/40 border-amber-200 text-gray-400" : "border-gray-300 focus:border-blue-500"}`}
                disabled={aiLoading}
                data-testid="resend-note-input"
              />
              {aiLoading && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="inline-flex items-center gap-2 text-xs text-amber-700 font-semibold">
                    <Sparkles size={12} className="animate-pulse" /> Drafting personalized note…
                  </div>
                </div>
              )}
            </div>
            {aiGenerated && !aiLoading && (
              <p className="text-[11px] text-amber-700 mt-1.5 flex items-center gap-1">
                <Sparkles size={11} /> AI-personalized draft. Tweak as you like.
              </p>
            )}
            {!aiGenerated && note && note !== share.personal_note && (
              <p className="text-[11px] text-blue-700 mt-1.5 flex items-center gap-1"><Check size={11} /> Your updated note will replace the original.</p>
            )}
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={close} className="flex-1" data-testid="resend-cancel-btn">Cancel</Button>
            <Button
              onClick={handleResend}
              disabled={loading}
              className="flex-1 bg-blue-900 hover:bg-blue-800 text-white font-bold"
              data-testid="resend-confirm-btn"
            >
              {loading ? "Sending…" : (<><Send size={13} className="mr-1.5" /> Resend Email</>)}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
